import { useCallback, useEffect, useState } from 'react';
import { Divider, Form, message, Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import RadioCards from 'components/RadioCards/RadioCards';
import { familyService } from 'services';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { ADULT_MARITAL_STATUS_OPTIONS } from '../household/FamilyAppHouseholdStructure';
import { getFirstName } from 'utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationMaritalStatus({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Form state
  const [form] = Form.useForm();
  const maritalStatusFormValue = Form.useWatch('maritalStatus', form);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.MARITAL_STATUS, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        // Determine if married based on marital status
        const isMarried =
          values.maritalStatus === ADULT_MARITAL_STATUS_OPTIONS.MARRIED ||
          values.maritalStatus === ADULT_MARITAL_STATUS_OPTIONS.SEPARATED;

        // Update applicant with marital status information
        const updatedApplicant = await familyService.addUpdateAdult(household?.id, application?.applicant?.id ?? null, {
          ...application?.applicant,
          married: isMarried,
          maritalStatus: values.maritalStatus,
          partnerStatus: values.partnerStatus,
        });

        const _newApplication = await familyService.saveApplication({
          ...application,
          applicant: updatedApplicant,
        });
        setApplication(_newApplication);

        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Show partner options for Single marital status
  const _showPartnerOptions = maritalStatusFormValue === ADULT_MARITAL_STATUS_OPTIONS.SINGLE;

  // Set initial form values
  useEffect(() => {
    form.setFieldsValue({
      maritalStatus: application?.applicant?.maritalStatus,
      partnerStatus: application?.applicant?.partnerStatus,
    });
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title="Marital Status" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? "What's your marital status?"
                : `What's ${getFirstName(application?.applicant)}'s marital status?`
            }
            className="my-12"
          />

          <Form.Item
            name="maritalStatus"
            rules={[
              {
                required: true,
                message: 'Please select a marital status',
              },
            ]}
          >
            <RadioCards
              data-testid="marital-status-cards"
              options={[
                {
                  label: 'Married',
                  value: ADULT_MARITAL_STATUS_OPTIONS.MARRIED,
                },
                {
                  label: 'Single',
                  value: ADULT_MARITAL_STATUS_OPTIONS.SINGLE,
                },
                {
                  label: 'Separated (legally)',
                  value: ADULT_MARITAL_STATUS_OPTIONS.SEPARATED,
                },
              ]}
              onChange={() => {
                form.setFieldsValue({
                  partnerStatus: undefined,
                });
              }}
            />
          </Form.Item>

          {_showPartnerOptions && (
            <>
              <Divider dashed className="my-12" />

              <Banner variant="inline" question="Who helps with the children?" className="mt-12 mb-8" />

              <Form.Item
                name="partnerStatus"
                rules={[
                  {
                    required: true,
                    message: 'Please select who helps with the children',
                  },
                ]}
              >
                <RadioCards
                  data-testid="partner-status-cards"
                  options={isCompletedBySponsor ? _partnerOptions.SPONSOR : _partnerOptions.ON_BEHALF}
                />
              </Form.Item>
            </>
          )}
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.MARITAL_STATUS, application, stepConfig);

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

const _partnerOptions = {
  SPONSOR: [
    {
      label: 'Co-parent',
      value: ADULT_MARITAL_STATUS_OPTIONS.CO_PARENT,
    },
    {
      label: 'Ex-spouse (divorced)',
      value: ADULT_MARITAL_STATUS_OPTIONS.DIVORCED_SHARED_CUSTODY,
    },
    {
      label: 'Power of Attorney',
      value: ADULT_MARITAL_STATUS_OPTIONS.POA,
    },
    {
      label: 'Just me',
      value: ADULT_MARITAL_STATUS_OPTIONS.SINGLE,
    },
  ],
  ON_BEHALF: [
    {
      label: 'Co-parent',
      value: ADULT_MARITAL_STATUS_OPTIONS.CO_PARENT,
    },
    {
      label: 'Ex-spouse (divorced)',
      value: ADULT_MARITAL_STATUS_OPTIONS.DIVORCED_SHARED_CUSTODY,
    },
    {
      label: 'Power of Attorney',
      value: ADULT_MARITAL_STATUS_OPTIONS.POA,
    },
  ],
};
